/**
 * Reusable sx fragments + design tokens layered on top of the theme.
 *
 * The theme defines raw palette / typography / motion tokens; this
 * file pins down the small set of *patterns* (chip flavours, mono
 * text-field overrides, icon sizes) so individual components don't
 * each invent their own. Centralising here means a single place to
 * change when the design language evolves.
 *
 * ─── Cyan vs gold ─────────────────────────────────────────────────
 *
 * Two accents, two domains:
 *
 *   • **Trace cyan** (`primary`/`accent`) owns *structural* state:
 *     selected, current, complete, in-flight, committed. Hover rims,
 *     focus rings, route-active highlights, "verified" / "synced",
 *     primary CTAs (`AddAffordance`).
 *
 *   • **Editorial gold** (`editorial`) owns *content* state:
 *     monetary value (wallet, credits, BOM cost), advisory or
 *     judgement-pending signals (stale version, lossy upload, AI
 *     suggestions), and "still being written" markers (Concept tier,
 *     Brief mid-edit, Draft kind).
 *
 * Use the `traceRim` / `traceChip` / `editorialRim` / `editorialChip`
 * mixins below so a single change to the rule propagates to every
 * callsite.
 */
import type { CSSObject, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

/* -------------------------------------------------------------------------- */
/*  Icon size tiers                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Three-tier icon-sizing scale. Use these instead of inline `fontSize:
 * 14` etc. so the visual rhythm stays consistent across the app.
 *
 *   • `inline`   — sits inside a chip / next to caption text (14px).
 *   • `support`  — action arrows, status indicators, tooltip glyphs (16px).
 *   • `section`  — overline-header glyphs that anchor a section (18px).
 */
export const ICON_SIZE = {
  inline: 14,
  support: 16,
  section: 18,
} as const;

/* -------------------------------------------------------------------------- */
/*  Chip flavours                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Tonal chip — primary-coloured fill at `accent.soft` (16% alpha)
 * with a primary-coloured border. The stronger flavour, used for
 * "this is an active / selected / important tag" — type chips on
 * connectors, owner-org pills, version badges (handled by their own
 * component).
 */
export function chipTonal(t: Theme): CSSObject {
  return {
    backgroundColor: t.palette.accent.soft,
    color: t.palette.primary.main,
    border: `1px solid ${alpha(t.palette.primary.main, 0.3)}`,
    fontWeight: 600,
    "& .MuiChip-icon": { color: t.palette.primary.main },
  };
}

/**
 * Subtle chip — `accent.softer` (8% alpha) fill, primary text. The
 * lighter flavour, used for "supporting metadata" — connector kind,
 * cable type, secondary status pills.
 */
export function chipSubtle(t: Theme): CSSObject {
  return {
    backgroundColor: t.palette.accent.softer,
    color: t.palette.primary.main,
    border: `1px solid ${alpha(t.palette.primary.main, 0.25)}`,
    "& .MuiChip-icon": { color: t.palette.primary.main },
  };
}

/**
 * Neutral chip — uses theme-default chip styling but with a faint
 * surface-tinted background. For metadata that isn't an accent
 * (e.g. PCB names in topology roster, "Manual" pill on connector
 * rows, system summary counts).
 */
export function chipNeutral(t: Theme): CSSObject {
  return {
    backgroundColor: alpha(t.palette.text.primary, 0.05),
    color: t.palette.text.secondary,
    border: `1px solid ${alpha(t.palette.text.primary, 0.1)}`,
  };
}

/* -------------------------------------------------------------------------- */
/*  Form input presets                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Slot props for a TextField that should render its content in a
 * monospaced font — ref designators, version labels, anything where
 * each character carries meaning. Apply via
 * `<TextField slotProps={{ htmlInput: { style: monoTextFieldStyle } }} />`.
 */
// Mirrors `theme.fonts.mono` (declared in features/theme/schemes/dark.ts).
// Duplicated here as a plain string because slotProps.htmlInput.style
// is a CSSProperties object — it can't accept a theme callback. Keep
// in sync with the theme token; both are derived from the same brand
// decision (JetBrains Mono first, with Geist Mono / system fallbacks).
export const monoTextFieldStyle = {
  fontFamily:
    '"JetBrains Mono", "Geist Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: "0.8125rem",
} as const;

/* -------------------------------------------------------------------------- */
/*  Blueprint texture                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Draftsman crosshatch grid for schematic surfaces, the Instrument Rack,
 * and any other "this is the working drawing" panel. Two thin orthogonal
 * gradients at ~3% phosphor opacity over the sunken surface. Cheap (no
 * raster), prints crisply at any zoom, scales with browser DPI.
 *
 * The 24px grid lines up with the 8px MUI spacing scale (3×) so chips
 * and pills sit on integer multiples of the grid line. Any time you
 * place an interactive element on a `blueprintBg()` surface the grid
 * reads as part of the layout, not noise.
 *
 * Apply via `sx={(t) => ({ ...blueprintBg(t) })}` on the container.
 */
export function blueprintBg(t: Theme): CSSObject {
  const phosphor = t.palette.accent.main;
  const line = alpha(phosphor, 0.04);
  return {
    backgroundColor: t.palette.surface.sunken,
    backgroundImage: `
      linear-gradient(${line} 1px, transparent 1px),
      linear-gradient(90deg, ${line} 1px, transparent 1px)
    `,
    backgroundSize: "24px 24px, 24px 24px",
    backgroundPosition: "-1px -1px, -1px -1px",
  };
}

/**
 * Editorial chip — the amber counterpart to `chipTonal`. Used for AI
 * provenance, EDA imports, suggested renames, and any moment where a
 * human or the agent made a judgement call. Visually distinct from the
 * phosphor accent so the eye learns to read the two apart.
 */
export function chipEditorial(t: Theme): CSSObject {
  return {
    backgroundColor: t.palette.editorial.soft,
    color: t.palette.editorial.main,
    border: `1px solid ${alpha(t.palette.editorial.main, 0.32)}`,
    fontWeight: 600,
    "& .MuiChip-icon": { color: t.palette.editorial.main },
  };
}

/* -------------------------------------------------------------------------- */
/*  Rim mixins — cyan (structural) and gold (content)                          */
/* -------------------------------------------------------------------------- */

/**
 * Inset trace-cyan rim — structural emphasis. Use on selected /
 * focused / complete / in-flight surfaces. Renders the cyan as an
 * inset boxShadow so it stacks on top of the hairline border without
 * shifting layout.
 *
 * @param strength 0.35 (default) for primary emphasis, 0.18 for a
 *   resting tint that just hints at the rim.
 */
export function traceRim(t: Theme, strength = 0.35): CSSObject {
  return {
    boxShadow: `inset 0 0 0 1px ${alpha(t.palette.primary.main, strength)}`,
  };
}

/**
 * Inset editorial-gold rim — content / judgement emphasis. Mirrors
 * `traceRim` but on the gold token. Use on Concept-tier badges,
 * mid-edit chips on draft surfaces, advisory banners, and AI
 * provenance markers.
 */
export function editorialRim(t: Theme, strength = 0.35): CSSObject {
  return {
    boxShadow: `inset 0 0 0 1px ${alpha(t.palette.editorial.main, strength)}`,
  };
}

/**
 * Trace-cyan chip — small mono ALL-CAPS pill on a soft-cyan ground
 * with an inset rim. Used inline in row chrome (version chips, tier
 * chips for Product / Regulated, "complete" markers).
 */
export function traceChip(t: Theme): CSSObject {
  return {
    fontFamily: t.fonts.mono,
    fontSize: "0.6875rem",
    fontWeight: 600,
    color: t.palette.primary.light,
    backgroundColor: alpha(t.palette.primary.main, 0.1),
    boxShadow: `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.35)}`,
    borderRadius: 6,
    padding: "1px 7px",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    fontVariantNumeric: "tabular-nums",
    lineHeight: 1.6,
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
  };
}

/**
 * Editorial-gold chip — same pill geometry as `traceChip` on the
 * editorial token. Used for AI provenance, monetary value, draft
 * markers, advisory states.
 */
export function editorialChip(t: Theme): CSSObject {
  return {
    fontFamily: t.fonts.mono,
    fontSize: "0.6875rem",
    fontWeight: 600,
    color: t.palette.editorial.contrastText,
    backgroundColor: alpha(t.palette.editorial.main, 0.14),
    boxShadow: `inset 0 0 0 1px ${alpha(t.palette.editorial.main, 0.4)}`,
    borderRadius: 6,
    padding: "1px 7px",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    fontVariantNumeric: "tabular-nums",
    lineHeight: 1.6,
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
  };
}

/* -------------------------------------------------------------------------- */
/*  Card padding tiers                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Two-tier card padding. Picks one of these instead of inventing
 * yet another `p: 1.25` / `p: 1.75` so vertical rhythm holds.
 *
 *   • `row`     — inner row cards (one connector / one connection).
 *   • `section` — outer section cards (a description editor, a links
 *                 editor as a whole).
 */
export const CARD_PADDING = {
  row: 1.5,
  section: 2,
} as const;
